import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import { branchDate, type JobStatus, type OrderStatus } from '@lensmira/domain'
import { formatDay } from '@lensmira/i18n'

import { formatWireForDisplay, parseMinorUnits } from '../money.js'
import { useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { jobStatusLabel, orderStatusLabel } from '../status-labels.js'

/**
 * Orders and jobs. The counter's landing screen. `WRK-001`, `JOB-002`, `BR-08`; `DESIGN-TOKENS.md`
 * `SC-01`: "The counter's landing screen is the work queue, not a dashboard."
 *
 * **There is no list endpoint yet.** `apps/api/src/orders/orders.controller.ts` has `POST /orders`,
 * `POST /orders/confirm`, `POST /orders/payments`, `POST /orders/void`, `POST /orders/refund`,
 * `POST /orders/returns` and one read, `GET /orders/returns/:orderId` (a policy lookup, not a
 * list). `apps/api/src/jobs/jobs.controller.ts` has no `@Get` at all. Neither controller can answer
 * "what needs doing today", so this screen is built against the typed shape below and a `loadOrders`
 * function the parent supplies, with `placeholderLoadOrders` as the clearly marked stand-in until
 * `GET /orders` exists. See `integrationNotes` in the handoff for the assumed response shape.
 *
 * **This is a read, so it does not go through the write queue.** `OFF-001a` is about writes; this
 * screen makes none. `loadOrders` is injected the same way `CustomerSearch` injects `doFetch` and
 * `SignIn` injects `doFetch`, except nothing here calls `fetch` itself, so, unlike
 * `CustomerSearch.tsx`, this file needs no entry in `eslint.config.js`'s `writesGoThroughTheQueue`
 * ignore list.
 *
 * **Order status does not borrow the job-lifecycle chip palette.** `DESIGN-TOKENS.md` §4.2 is
 * headed "Job lifecycle colours" and its ten buckets (`draft`, `ordered`, `lab`, `transit`, `check`,
 * `ready`, `done`, `hold`, `remake`, `void`) read almost 1:1 onto Appendix C.2's job states, so
 * `JOB_STATUS_CHIP` below uses them. Appendix C.1's eleven order states do not have a documented
 * palette of their own, and forcing them onto the job buckets produces wrong answers, not just
 * imprecise ones: a refunded order in `--lm-status-remake`'s red reads as a QC failure to someone
 * scanning the queue. So an order's chip is neutral by default (`CL-02` already guarantees the
 * status is legible from its text label alone) and only the states that need a shopkeeper's
 * attention take colour: `on_hold` warns, `refunded` takes the danger tone, `cancelled` and
 * `expired` take the void tone.
 *
 * **Overdue compares calendar dates, not instants.** `promisedOn` is assumed to be a plain
 * `YYYY-MM-DD` string, not a timestamp. Pakistan is UTC+5, and `new Date('2026-09-05').getTime()`
 * parses as UTC midnight, five hours before the shop's own midnight: a job promised "today" would
 * read as overdue from 5am PKT onward, every day, which is a false alarm on a screen whose entire
 * job is to be trusted at a glance. Comparing the ISO date strings lexicographically avoids parsing
 * either side as an instant, so there is no timezone to get wrong.
 *
 * `on_hold`, `chasing`, `qc_failed` and `remake_raised` are deliberately left overdue-eligible.
 * `WRK-002`: "Items cannot be dismissed without an outcome." A queue that stops flagging a late job
 * the moment someone puts it on hold would let a shop park its way out of the one thing this screen
 * exists to make obvious.
 *
 * **`totalMinor` and `outstandingMinor` arrive computed.** This screen never subtracts one from the
 * other. `MR-04` puts rounding at exactly three points in the calculation chain and none of them is
 * a counter screen, so any money arithmetic here would be a fourth point invented by this file.
 */

export interface JobSummary {
  readonly id: string
  readonly status: JobStatus
  /** Calendar date, `YYYY-MM-DD`. Absent when the lab has not yet committed to one (`JOB-004`). */
  readonly promisedOn?: string
}

export interface OrderSummary {
  readonly id: string
  /** The shop-facing order number, not the fiscal invoice number `orders.controller.ts` says is
   *  taken only at `confirm`. Whether a `draft` order has one of these yet is an open question,
   *  named in `openQuestions`. */
  /**
   * The invoice number, or null until `TAX-002` issues one.
   *
   * Null rather than a placeholder: an English string in a data field cannot be translated and
   * leaks into any sentence that interpolates it. The label is this app's, which is what
   * `orders-list.service.ts` asks for in words.
   */
  readonly number: string | null
  readonly customerName: string
  readonly status: OrderStatus
  /** ISO 4217, shared by both amounts below. */
  readonly currency: string
  /** Minor units, as a string. Never parsed into a number in this file. */
  readonly totalMinor: string
  /** Minor units, as a string. Computed by the server; see the note above. */
  readonly outstandingMinor: string
  /**
   * What a refund gave back. Zero on an order that has never been refunded.
   *
   * **Beside the invoice, never netted into it.** `outstandingMinor` above is what the shop can
   * still collect, and on a fully refunded order that is genuinely zero: the goods came back and the
   * money went back, so a balance here would be a debt nobody can pay, and `POS-002` puts that
   * figure on the customer's record and the branch money-owed report. But "Paid in full" is the
   * wrong sentence for a sale that was reversed, which is what this screen used to say. Carrying
   * both figures is what lets it say what actually happened, and `POS-005` deletes no record.
   */
  readonly refundedMinor: string
  /** `BR-08`: attribution travels with the record rather than being inferred from who is signed in
   *  now, because the person reviewing the queue is often not the person who took the order. */
  /**
   * Who sold it and where, when the server can say.
   *
   * **Both optional, because `GET /orders` sends neither.** The list is already scoped to one
   * branch, `branchId` being a required parameter, so the branch is what the caller asked for
   * rather than something the row carries; the read layer fills it in from the shell's own context.
   * The staff member has nowhere to come from at all: `sales_order.sold_by_staff_id` is stored but
   * the read does not join `staff` for a name, so this stays absent until it does.
   *
   * They were declared required and rendered unconditionally, which is the fourth instance in this
   * codebase of a screen typing a field the endpoint has never sent. Found by opening the list in a
   * browser against a real order.
   */
  readonly branchName?: string
  readonly staffName?: string
  readonly jobs: readonly JobSummary[]
}

export interface OrderListProps {
  /**
   * The branch's timezone, for `LOC-004`.
   *
   * Appendix J: an instant renders in the branch's zone, never the browser's. A plain `date`
   * column renders in none, which is what `formatDay` is for.
   */
  readonly timezone: string
  readonly loadOrders: () => Promise<readonly OrderSummary[]>
  /**
   * Open one order.
   *
   * Optional, so the list is still a list without it: this screen has no idea how an order detail is
   * loaded and must not acquire one. `WRK-002` makes a queue entry a thing somebody acts on, and an
   * order nobody can open from the list is a list of things to write down and find again.
   */
  readonly onOpen?: (orderId: string) => void
  /**
   * Open one job.
   *
   * Separate from `onOpen`, because a job is not an order: `JOB-005a`'s quality gate, the lab
   * status and the collection all live on the job, and the order row is where the counter first
   * sees that one is waiting.
   */
  readonly onOpenJob?: (jobId: string) => void
  /**
   * Open one quote.
   *
   * A third callback and not a flag on `onOpen`, because `DSP-001` is a different record to act on:
   * a quote is an order in `quoted`, so it is listed here, but what a shopkeeper does with one is
   * convert it or record that it has run out. Offered only on a `quoted` row, which is what keeps
   * the list from showing a door that leads nowhere on the other eight statuses.
   */
  readonly onOpenQuote?: (orderId: string) => void
}

/**
 * PLACEHOLDER. Stands in for `GET /orders` until it exists. Returns no rows rather than throwing,
 * so a screen wired to this shows the empty state rather than the error state, which is the honest
 * answer to "what does the queue look like before the endpoint is built."
 */
export const placeholderLoadOrders: OrderListProps['loadOrders'] = () => Promise.resolve([])

type OrderChipTone = 'neutral' | 'warn' | 'danger' | 'void'

/** Appendix C.1 read onto the four tones a neutral order chip can take. See the header comment. */
const ORDER_STATUS_TONE: Readonly<Record<OrderStatus, OrderChipTone>> = {
  draft: 'neutral',
  quoted: 'neutral',
  confirmed: 'neutral',
  partially_paid: 'neutral',
  paid: 'neutral',
  fulfilled: 'neutral',
  closed: 'neutral',
  on_hold: 'warn',
  refunded: 'danger',
  expired: 'void',
  cancelled: 'void',
}

type JobChipBucket =
  'draft' | 'ordered' | 'lab' | 'transit' | 'check' | 'ready' | 'done' | 'hold' | 'remake' | 'void'

/** Appendix C.2 read onto `DESIGN-TOKENS.md` §4.2's ten job-lifecycle chip colours. */
const JOB_STATUS_CHIP: Readonly<Record<JobStatus, JobChipBucket>> = {
  draft: 'draft',
  ordered: 'ordered',
  sent: 'lab',
  acknowledged: 'lab',
  in_production: 'lab',
  chasing: 'hold',
  shipped: 'transit',
  received: 'transit',
  qc: 'check',
  qc_failed: 'remake',
  remake_raised: 'remake',
  ready: 'ready',
  collected: 'done',
  on_hold: 'hold',
  cancelled: 'void',
}

/** `collected` and `cancelled` are the only statuses a promised date stops applying to. */
const JOB_STATUSES_NEVER_OVERDUE: ReadonlySet<JobStatus> = new Set(['collected', 'cancelled'])

function isJobOverdue(job: JobSummary, todayIso: string): boolean {
  if (job.promisedOn === undefined) return false
  if (JOB_STATUSES_NEVER_OVERDUE.has(job.status)) return false
  return job.promisedOn < todayIso
}

/*
 * **Today is the branch's day, not the device's.** §16.3; `LOC-004`; Appendix J.
 *
 * This read the tablet's own clock, which is right only while the tablet agrees with the branch.
 * An owner looking at a branch in another zone, or a tablet whose timezone was never set, judged
 * "overdue" against the wrong day: a job promised for today showed the red flag, or one a day late
 * did not. `branchDate` is the same function the ledger uses to decide which day a sale belongs
 * to, so the queue and the books agree about when a day ends.
 */

/**
 * A minor-unit wire string to a display string, thousands-grouped with an explicit currency code.
 *
 * `../money.ts` is "shared by every screen" per its own header, and its `parseMinorUnits` and
 * `formatWireForDisplay` is the exact, no-`Number()` path from a wire string to a `bigint` and on
 * to grouped digits at the currency's own decimal places (`MR-01`, `LOC-004`).
 *
 * **This function used to be that formatting, and its own comment was what let the app drift.** It
 * said the bare form "is what a card in a list benefits from and a single payment field does not",
 * and five screens read that as licence to each decide grouping for themselves. Two of them decided
 * differently, so the same PKR 7,500 read as `PKR 7,500.00` here and `PKR 7500.00` on the Z-report
 * for the same sale. The distinction does not survive contact with the code either: not one of the
 * ungrouped sites was a payment field. They were a Z-report, an order receipt and a read-only hint.
 * Kept exported, because the tests call it directly.
 */
export function formatOrderAmount(minor: string, currency: string): string {
  return formatWireForDisplay(minor, currency)
}

type LoadState =
  | { readonly kind: 'loading' }
  // `message` is absent for a throw that was not an `Error`; the sentence is chosen at render time
  // so a language change does not need a reload to take effect.
  | { readonly kind: 'error'; readonly message?: string }
  | { readonly kind: 'loaded'; readonly orders: readonly OrderSummary[] }

export function OrderList({
  loadOrders,
  timezone,
  onOpen,
  onOpenJob,
  onOpenQuote,
}: OrderListProps): JSX.Element {
  const { t, locale } = useTranslation()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  // Bumped to re-run the load effect on "Try again", rather than duplicating the fetch logic in an
  // onClick handler.
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setState({ kind: 'loading' })
    loadOrders()
      .then((orders) => {
        if (!cancelled) setState({ kind: 'loaded', orders })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setState({
          kind: 'error',
          ...(error instanceof Error ? { message: error.message } : {}),
        })
      })
    return () => {
      cancelled = true
    }
  }, [loadOrders, attempt])

  const today = branchDate(Date.now(), timezone)

  return (
    <section className="lm-screen" aria-labelledby="order-list-heading">
      <h1 id="order-list-heading" className="lm-screen__heading">
        {t('orderList.heading')}
      </h1>

      {/*
        §8 `ST-02`'s six states, through one component. What this screen had was a grey line for
        loading, an error with a retry, and one sentence for empty; what it gains is a skeleton
        shaped like the cards it replaces and a sentence for a read Appendix B refused, which used
        to render as "no orders" and is a different and false statement.
      */}
      <DataSurface
        state={
          state.kind === 'loading'
            ? { kind: 'loading' }
            : state.kind === 'error'
              ? {
                  kind: 'error',
                  message:
                    state.message === undefined
                      ? t('orderList.loadFailedNoDetail')
                      : t('orderList.loadFailed', { message: state.message }),
                  onRetry: () => setAttempt((a) => a + 1),
                }
              : state.orders.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={4}
        what={t('orderList.what')}
        emptyMessage={t('orderList.empty')}
      >
        <ul className="lm-order-list">
          {(state.kind === 'loaded' ? state.orders : []).map((order) => {
            const overdueJobCount = order.jobs.filter((job) => isJobOverdue(job, today)).length
            const orderClassName =
              overdueJobCount > 0 ? 'lm-order-card lm-order-card--overdue' : 'lm-order-card'
            const outstandingClassName =
              order.outstandingMinor === '0'
                ? 'lm-order-card__amount-value'
                : 'lm-order-card__amount-value lm-order-card__amount-value--owed'

            /**
             * What to call this order, and `dir="ltr"` only when it really is a number.
             *
             * `TAX-002` issues the invoice number partway through an order's life, so a draft has
             * none. The wire used to answer with the English string `(not yet invoiced)`, which
             * left six articles in this list sharing one accessible name and an English phrase on
             * an Urdu page. A label is a label: it takes the page's direction.
             */
            const orderName = order.number ?? t('order.notInvoiced')
            /**
             * The buttons name the customer when there is no number.
             *
             * `Open order {number}` with a label dropped into it reads "Open order Not invoiced
             * yet", and six of those are six buttons a screen reader cannot tell apart, which is
             * what the number was for. The customer is the thing that distinguishes the row.
             */
            const openName =
              order.number === null
                ? t('orderList.openOrderNoNumber', { customer: order.customerName })
                : t('orderList.openOrder', { number: order.number })
            const openQuoteName =
              order.number === null
                ? t('orderList.openQuoteNoNumber', { customer: order.customerName })
                : t('orderList.openQuote', { number: order.number })

            return (
              <li key={order.id}>
                {/* Named by the number **and** the customer.
                    Six un-invoiced orders all answered "(not yet invoiced)" and now all answer
                    "Not invoiced yet", so a screen reader hears one name six times and the list is
                    unusable without sight. The number alone was only ever enough once `TAX-002`
                    had issued one. */}
                <article
                  className={orderClassName}
                  aria-labelledby={`order-number-${order.id} order-customer-${order.id}`}
                >
                  <div className="lm-order-card__header">
                    <span
                      id={`order-number-${order.id}`}
                      className="lm-order-card__number"
                      {...(order.number === null ? {} : { dir: 'ltr' as const })}
                    >
                      {orderName}
                    </span>
                    <span
                      className="lm-chip lm-chip--order"
                      data-tone={ORDER_STATUS_TONE[order.status]}
                    >
                      {orderStatusLabel(t, order.status)}
                    </span>
                    {onOpen ? (
                      <button
                        type="button"
                        className="lm-button lm-button--secondary lm-order-card__open"
                        onClick={() => {
                          onOpen(order.id)
                        }}
                      >
                        {/* The number is in the accessible name, not only on screen: a list of
                            twenty identical "Open" buttons is unusable with a screen reader, and
                            release gate 1-s keyboard-first stance applies past the Rx grid. The
                            name is one whole sentence because "Open" + " order N" cannot be
                            assembled from two pieces in a language with another word order. */}
                        <span aria-hidden="true">{t('common.open')}</span>
                        <span className="lm-visually-hidden">
                          {openName}
                        </span>
                      </button>
                    ) : null}
                    {onOpenQuote && order.status === 'quoted' ? (
                      <button
                        type="button"
                        className="lm-button lm-button--secondary lm-order-card__open"
                        onClick={() => {
                          onOpenQuote(order.id)
                        }}
                      >
                        {/* The number is in the accessible name for the same reason as the Open
                            button above: twenty identical buttons are unusable with a screen
                            reader, and the whole sentence is one message. */}
                        <span aria-hidden="true">{t('orderList.quote')}</span>
                        <span className="lm-visually-hidden">
                          {openQuoteName}
                        </span>
                      </button>
                    ) : null}
                  </div>

                  <p id={`order-customer-${order.id}`} className="lm-order-card__customer">
                    {order.customerName}
                  </p>

                  <dl className="lm-order-card__totals">
                    <div className="lm-order-card__amount">
                      <dt>{t('common.total')}</dt>
                      <dd dir="ltr" className="lm-order-card__amount-value">
                        {formatOrderAmount(order.totalMinor, order.currency)}
                      </dd>
                    </div>
                    <div className="lm-order-card__amount">
                      <dt>{t('common.outstanding')}</dt>
                      <dd dir="ltr" className={outstandingClassName}>
                        {formatOrderAmount(order.outstandingMinor, order.currency)}
                      </dd>
                    </div>
                    {/*
                      Shown only when there is one, and beside the invoice rather than netted into
                      it. An order refunded in full reads "Outstanding PKR 0.00" like a paid one
                      otherwise, and the two are not the same thing to a shopkeeper.
                    */}
                    {parseMinorUnits(order.refundedMinor) > 0n ? (
                      <div className="lm-order-card__amount">
                        <dt>{t('orderList.refunded')}</dt>
                        <dd dir="ltr" className="lm-order-card__amount-value">
                          {formatOrderAmount(order.refundedMinor, order.currency)}
                        </dd>
                      </div>
                    ) : null}
                  </dl>

                  {/* BR-08: recorded attribution, shown as given rather than inferred from who is
                      signed in on this device now. */}
                  <p className="lm-order-card__attribution">
                    {[order.branchName, order.staffName].filter(Boolean).join(' · ')}
                  </p>

                  {order.jobs.length > 0 ? (
                    <ul className="lm-job-list">
                      {order.jobs.map((job) => {
                        const overdue = isJobOverdue(job, today)
                        const rowClassName = overdue
                          ? 'lm-job-row lm-job-row--overdue'
                          : 'lm-job-row'
                        return (
                          <li key={job.id} className={rowClassName}>
                            <span
                              className="lm-chip lm-chip--job"
                              data-chip={JOB_STATUS_CHIP[job.status]}
                            >
                              {jobStatusLabel(t, job.status)}
                            </span>
                            {job.promisedOn !== undefined ? (
                              <span className="lm-job-row__promised" dir="ltr">
                                {t('common.promised', { date: formatDay(job.promisedOn, timezone, locale) })}
                              </span>
                            ) : null}
                            {overdue ? (
                              <span className="lm-job-row__overdue-flag">
                                {t('common.overdue')}
                              </span>
                            ) : null}
                            {onOpenJob ? (
                              <button
                                type="button"
                                className="lm-button lm-button--secondary lm-job-row__open"
                                onClick={() => {
                                  onOpenJob(job.id)
                                }}
                              >
                                {/* The status is in the accessible name: a list of identical "Open"
                                    buttons is unusable with a screen reader. */}
                                <span aria-hidden="true">{t('common.open')}</span>
                                <span className="lm-visually-hidden">
                                  {order.number === null
                                    ? t('orderList.openJobNoNumber', {
                                        customer: order.customerName,
                                        status: jobStatusLabel(t, job.status),
                                      })
                                    : t('orderList.openJob', {
                                        number: order.number,
                                        status: jobStatusLabel(t, job.status),
                                      })}
                                </span>
                              </button>
                            ) : null}
                          </li>
                        )
                      })}
                    </ul>
                  ) : null}
                </article>
              </li>
            )
          })}
        </ul>
      </DataSurface>
    </section>
  )
}
